/**
 * Секция «История анализов» в карточке сотрудника (ADR-019, TM-5743cb35).
 *
 * Тянет `GET /api/employees/{id}/analyses`, поллит каждые 30с пока есть
 * pending/running записи. Каждая запись — карточка со статус-бейджем,
 * запросом, превью результата и кнопкой «Открыть» (открывает viewer
 * с полным текстом через `GET /api/manager_analyses/{id}`).
 */

import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { getManagerAnalysis, listManagerAnalyses } from "@/api/lookups";
import type {
  ManagerAnalysisStatus,
  ManagerAnalysisSummary,
  UUID,
} from "@/api/types";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export interface AnalysisHistorySectionProps {
  employeeId: UUID;
}

const POLL_INTERVAL_MS = 30_000;

function hasActiveStatus(rows: ManagerAnalysisSummary[]): boolean {
  return rows.some((r) => r.status === "pending" || r.status === "running");
}

const STATUS_CLASSES: Record<ManagerAnalysisStatus, string> = {
  pending: "bg-muted text-muted-foreground",
  running: "bg-blue-100 text-blue-800",
  done: "bg-green-100 text-green-800",
  error: "bg-red-100 text-red-800",
};

export function AnalysisHistorySection({
  employeeId,
}: AnalysisHistorySectionProps) {
  const { t, i18n } = useTranslation();
  const [openId, setOpenId] = useState<UUID | null>(null);

  const historyQ = useQuery({
    queryKey: ["manager-analyses", employeeId],
    queryFn: () => listManagerAnalyses(employeeId, { limit: 50 }),
    // Polling раз в 30с пока есть pending/running.
    refetchInterval: (q) =>
      q.state.data !== undefined && hasActiveStatus(q.state.data)
        ? POLL_INTERVAL_MS
        : false,
  });

  const rows = historyQ.data ?? [];
  const fmtDate = (iso: string | null): string =>
    iso === null ? "" : new Date(iso).toLocaleString(i18n.language);

  return (
    <section className="space-y-2" data-testid="employee-card-analysis-history">
      <h2 className="text-lg font-semibold">
        {t("managerAnalysis.section.history", { count: rows.length })}
      </h2>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {t("managerAnalysis.section.empty")}
        </p>
      ) : (
        <div className="space-y-2">
          {rows.map((row) => (
            <Card key={row.id} data-testid={`analysis-row-${row.id}`}>
              <CardContent className="space-y-2 p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_CLASSES[row.status]}`}
                    data-testid={`analysis-row-status-${row.id}`}
                  >
                    {t(`managerAnalysis.status.${row.status}`)}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {t("managerAnalysis.history.createdAt", {
                      value: fmtDate(row.created_at),
                    })}
                  </span>
                  {row.finished_at !== null ? (
                    <span className="text-xs text-muted-foreground">
                      {t("managerAnalysis.history.finishedAt", {
                        value: fmtDate(row.finished_at),
                      })}
                    </span>
                  ) : null}
                </div>

                <p className="line-clamp-2 text-sm text-foreground">
                  {row.query_text}
                </p>

                {row.status === "done" && row.result_preview !== null ? (
                  <p className="line-clamp-3 whitespace-pre-line text-xs text-muted-foreground">
                    {row.result_preview}
                  </p>
                ) : null}

                {row.status === "error" && row.error_message !== null ? (
                  <p className="text-xs text-destructive">
                    {t("managerAnalysis.history.errorMessage", {
                      message: row.error_message,
                    })}
                  </p>
                ) : null}

                {row.status === "done" ? (
                  <div>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => setOpenId(row.id)}
                      data-testid={`analysis-row-open-${row.id}`}
                    >
                      {t("managerAnalysis.history.openButton")}
                    </Button>
                  </div>
                ) : null}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <AnalysisViewerDialog
        analysisId={openId}
        onClose={() => setOpenId(null)}
      />
    </section>
  );
}

interface AnalysisViewerDialogProps {
  analysisId: UUID | null;
  onClose: () => void;
}

function AnalysisViewerDialog({
  analysisId,
  onClose,
}: AnalysisViewerDialogProps) {
  const { t, i18n } = useTranslation();
  const viewerQ = useQuery({
    queryKey: ["manager-analysis", analysisId],
    queryFn: () => getManagerAnalysis(analysisId as UUID),
    enabled: analysisId !== null,
  });

  const data = viewerQ.data;
  const dateLabel =
    data !== undefined
      ? new Date(data.created_at).toLocaleString(i18n.language)
      : "";

  return (
    <Dialog open={analysisId !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-h-[80vh] max-w-3xl overflow-y-auto"
        data-testid="analysis-viewer-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            {t("managerAnalysis.viewer.title", { date: dateLabel })}
          </DialogTitle>
          <DialogDescription>
            {data !== undefined
              ? [
                  t("managerAnalysis.history.tokens", {
                    input: data.input_tokens ?? "?",
                    output: data.output_tokens ?? "?",
                  }),
                  data.llm_model !== null
                    ? t("managerAnalysis.history.model", {
                        name: data.llm_model,
                      })
                    : null,
                ]
                  .filter(Boolean)
                  .join(" · ")
              : ""}
          </DialogDescription>
        </DialogHeader>

        {data === undefined ? (
          <p className="text-sm text-muted-foreground">…</p>
        ) : (
          <div className="space-y-3 text-sm">
            <div>
              <h3 className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {t("managerAnalysis.viewer.queryHeading")}
              </h3>
              <p className="whitespace-pre-line">{data.query_text}</p>
            </div>
            <div>
              <h3 className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {t("managerAnalysis.viewer.resultHeading")}
              </h3>
              <pre className="whitespace-pre-wrap break-words rounded-md bg-muted/40 p-3 font-sans text-sm">
                {data.result_text ?? ""}
              </pre>
            </div>
          </div>
        )}

        <div className="flex justify-end pt-2">
          <Button type="button" variant="outline" onClick={onClose}>
            {t("managerAnalysis.viewer.closeButton")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
